import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Trash2, History, Printer, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const fmtRp = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n || 0);

export default function ZakatRiwayatPane({ mosqueId }) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [jenis, setJenis] = useState('');
  const [loading, setLoading] = useState(true);
  const LIMIT = 20;

  const load = async (pg = 1, search = q, filterJenis = jenis) => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API_URL}/api/zakat-ramadan/transactions`, {
        ...cfg, params: { q: search || undefined, jenis: filterJenis || undefined, page: pg, limit: LIMIT }
      });
      setData(res.data);
      setTotal(res.total);
      setPage(pg);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(1); }, [mosqueId]);

  const handleDelete = async (id, nomor) => {
    if (!window.confirm(`Hapus transaksi ${nomor}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/zakat-ramadan/transactions/${id}`, cfg);
      toast.success('Transaksi dihapus');
      load(page);
    } catch { toast.error('Gagal menghapus'); }
  };

  return (
    <div className="space-y-4" data-testid="zakat-riwayat">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-stone-400" />
          <input placeholder="Cari nama / nomor..." value={q}
            onChange={e => { setQ(e.target.value); load(1, e.target.value, jenis); }}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none" />
        </div>
        <select value={jenis} onChange={e => { setJenis(e.target.value); load(1, q, e.target.value); }}
          className="px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-emerald-500 outline-none bg-white">
          <option value="">Semua Jenis</option>
          <option value="beras">Beras</option>
          <option value="uang">Uang</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-100 flex items-center gap-2">
          <History className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-semibold text-stone-700">{total} Transaksi</span>
        </div>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data.length === 0 ? (
          <div className="p-10 text-center text-stone-400 text-sm">Belum ada riwayat transaksi</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-xs text-stone-500">
                <tr>
                  <th className="px-4 py-3 text-left">No. Transaksi</th>
                  <th className="px-4 py-3 text-left">Tanggal</th>
                  <th className="px-4 py-3 text-left">Muzakki</th>
                  <th className="px-4 py-3 text-center">Jiwa</th>
                  <th className="px-4 py-3 text-left">Jenis</th>
                  <th className="px-4 py-3 text-right">Zakat</th>
                  <th className="px-4 py-3 text-right">Infak</th>
                  <th className="px-4 py-3 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {data.map(row => (
                  <tr key={row.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-stone-600">{row.nomor_transaksi}</td>
                    <td className="px-4 py-3 text-stone-600 whitespace-nowrap">{row.tanggal}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-stone-800">{row.nama_kk}</p>
                      {row.phone && <p className="text-xs text-stone-400">{row.phone}</p>}
                    </td>
                    <td className="px-4 py-3 text-center font-semibold text-emerald-700">{row.jumlah_jiwa}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${row.jenis_pembayaran === 'beras' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                        {row.jenis_pembayaran === 'beras' ? 'Beras' : 'Uang'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-stone-800">
                      {row.jenis_pembayaran === 'beras' ? `${row.total_zakat_beras} kg` : fmtRp(row.total_zakat_uang)}
                    </td>
                    <td className="px-4 py-3 text-right text-rose-600">
                      {row.has_infak ? fmtRp(row.nominal_infak) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleDelete(row.id, row.nomor_transaksi)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors mx-auto block">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > LIMIT && (
          <div className="px-5 py-3 border-t border-stone-100 flex items-center justify-between text-xs text-stone-500">
            <span>Hal. {page} dari {Math.ceil(total / LIMIT)}</span>
            <div className="flex gap-2">
              {page > 1 && <button onClick={() => load(page - 1)} className="px-3 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50">← Prev</button>}
              {page < Math.ceil(total / LIMIT) && <button onClick={() => load(page + 1)} className="px-3 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50">Next →</button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
